import { z } from 'zod';
import { currencyStepCents, roundToCurrencyStep } from './currency.ts';

/**
 * What a recurring per-visit quote adds to its first visit, because a first
 * clean almost always takes longer: a fixed amount in the quote's currency, or
 * basis points on top of the regular visit before tax. `value` 0 is allowed:
 * a first clean priced only by its first-visit-only tasks.
 *
 * Stored on the quote (`first_clean_price_json`). Its presence is what turns
 * first clean pricing on: the server then rebuilds the first-visit and
 * regular-visit scopes from the quote on every save (see
 * `buildQuoteFirstClean`), and booking charges the first one on the first
 * visit only.
 */
export const firstCleanPriceSchema = z
  .object({
    kind: z.enum(['fixed', 'percent']),
    value: z.number().int().min(0).max(100_000_000),
  })
  .strict()
  .refine(
    (value) => value.kind === 'fixed' || value.value <= 100_000,
    'The first clean percent must be 1,000% or less',
  );
export type FirstCleanPrice = z.infer<typeof firstCleanPriceSchema>;

export function readFirstCleanPrice(json: string | null | undefined): FirstCleanPrice | null {
  return json ? firstCleanPriceSchema.parse(JSON.parse(json)) : null;
}

/**
 * A quote's first clean price as the editor holds it. A draft kept in the
 * browser from before 0105 has no `firstCleanPrice` and still carries it
 * inside its area pricing; reading it from there keeps a replayed draft from
 * clearing it.
 */
export function quoteFirstCleanPrice(quote: {
  firstCleanPrice?: FirstCleanPrice | null;
  areaPricing?: { firstClean?: FirstCleanPrice | null } | null;
}): FirstCleanPrice | null {
  return quote.firstCleanPrice !== undefined ? quote.firstCleanPrice : (quote.areaPricing?.firstClean ?? null);
}

/** Only a per-visit quote charges a first clean; a monthly price has no first visit to charge. */
export function chargeableFirstCleanPrice(quote: {
  first_clean_price_json?: string | null;
  billing_basis?: string;
}): FirstCleanPrice | null {
  if (quote.billing_basis !== undefined && quote.billing_basis !== 'per_visit') return null;
  return readFirstCleanPrice(quote.first_clean_price_json);
}

export function firstCleanPriceProblem(value: FirstCleanPrice | null | undefined, currencyCode: string): string | null {
  if (value?.kind === 'fixed' && value.value % currencyStepCents(currencyCode) !== 0)
    return 'The first clean amount must use the supported precision of the quote currency.';
  return null;
}

/**
 * The first clean extra the customer is charged: a fixed amount, or a percent
 * of the regular visit before tax. It is charged as a taxable line on the
 * first visit, so tax is worked out on it like on any other charge.
 */
export function firstCleanExtraCents(
  regularBeforeTaxCents: number,
  firstClean: FirstCleanPrice,
  currencyCode = 'USD',
): number {
  const stepCents = currencyStepCents(currencyCode);
  const raw =
    firstClean.kind === 'fixed'
      ? firstClean.value
      : Math.round((Math.max(0, regularBeforeTaxCents) * firstClean.value) / 10_000);
  return Math.round(raw / stepCents) * stepCents;
}

export interface FirstCleanPricingColumns {
  fixed_fees_cents: number;
  taxable_subtotal_cents: number;
  tax_cents: number;
  calculated_total_cents: number;
  total_cents: number;
}

/**
 * A visit's pricing with the first clean extra added as a taxable charge.
 * Worked out from the visit's own priced columns rather than by pricing the
 * quote again, so a percentage discount is never applied to an extra that was
 * already worked out from the discounted price. Tax follows the engine's rule
 * on the combined taxable subtotal; the extra carries the difference that
 * makes, not its own separately rounded tax.
 */
export function withFirstCleanExtra<T extends FirstCleanPricingColumns>(
  pricing: T,
  extraCents: number,
  taxRateBasisPoints: number,
  currencyCode: string,
): T {
  const taxOn = (cents: number) => roundToCurrencyStep(Math.round((cents * taxRateBasisPoints) / 10_000), currencyCode);
  const taxable = pricing.taxable_subtotal_cents;
  const extraTax = taxOn(taxable + extraCents) - taxOn(taxable);
  return {
    ...pricing,
    fixed_fees_cents: pricing.fixed_fees_cents + extraCents,
    taxable_subtotal_cents: pricing.taxable_subtotal_cents + extraCents,
    tax_cents: pricing.tax_cents + extraTax,
    calculated_total_cents: pricing.calculated_total_cents + extraCents + extraTax,
    total_cents: pricing.total_cents + extraCents + extraTax,
  };
}

/**
 * Whether a task counts toward the regular visit. A first-visit-only task is
 * left out of the regular price, minutes and checklist while the quote has a
 * first clean price, and priced like any other task when it has none.
 */
export function countsOnRegularVisit(firstCleanOn: boolean, firstVisitOnly: boolean | undefined): boolean {
  return !(firstCleanOn && firstVisitOnly === true);
}

/**
 * Whether a room is on the regular visit at all. One whose selected tasks are
 * all first-visit-only is cleaned on the first visit alone, so its own price
 * and typed time go with those tasks rather than onto every regular visit.
 * A room with no task selected keeps its own price on both, as it always has.
 */
export function roomOnRegularVisit(
  firstCleanOn: boolean,
  tasks: readonly { isSelected: boolean; firstVisitOnly?: boolean | undefined }[],
): boolean {
  if (!firstCleanOn) return true;
  const selected = tasks.filter((task) => task.isSelected);
  return selected.length === 0 || selected.some((task) => task.firstVisitOnly !== true);
}

/**
 * The first visit's total from two engine results, the way the quote save
 * builds it: the first visit priced with its first-visit-only tasks, plus the
 * extra worked out on the regular visit's price before tax. For the editor's
 * live preview; the saved offer is the Worker's.
 */
export function firstVisitPreview(
  regular: { totalCents: number; taxCents: number },
  firstVisit: { fixedFeesCents: number; taxableSubtotalCents: number; taxCents: number; totalCents: number },
  price: FirstCleanPrice,
  taxRateBasisPoints: number,
  currencyCode: string,
): { extraCents: number; totalCents: number } {
  const extraCents = firstCleanExtraCents(regular.totalCents - regular.taxCents, price, currencyCode);
  const priced = withFirstCleanExtra(
    {
      fixed_fees_cents: firstVisit.fixedFeesCents,
      taxable_subtotal_cents: firstVisit.taxableSubtotalCents,
      tax_cents: firstVisit.taxCents,
      calculated_total_cents: firstVisit.totalCents,
      total_cents: firstVisit.totalCents,
    },
    extraCents,
    taxRateBasisPoints,
    currencyCode,
  );
  return { extraCents, totalCents: priced.total_cents };
}
